import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { fasCheck } from 'shared/@generated/icons/fasCheck';
import { fasTimes } from 'shared/@generated/icons/fasTimes';
import { View } from 'react-native';
import { Props } from 'shared/@types';
import { H1, H2 } from 'shared/components/title';
import { Image } from 'shared/components/image';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { PaymentBrands } from 'shared/components/payment-brands';
import { useTranslation } from 'react-i18next';
import catPeace from 'shared/assets/doodles/cat-peace.png';
import { Text } from 'shared/components/text';
import { PurchaseAgreement } from 'shared/components/illustrations/purchase-agreement';
import { Reservation } from 'shared/components/illustrations/reservation';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import { useRoute } from 'shared/library/routes/route';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { SvgImage } from 'shared/components/svg-image';
import bankidSe from 'shared/assets/brands/bankid-se.png';
import mitid from 'shared/assets/brands/mitid.png';
import ftn from 'shared/assets/brands/ftn.png';
import bankidNo from 'shared/assets/brands/bankid-no.png';
import stripe from 'shared/assets/brands/stripe.png';
import handelsbanken from 'shared/assets/brands/handelsbanken.png';
import logo from 'shared/assets/logo.svg';
import { clsx } from 'clsx';
import { withNativeGradientFallback } from 'shared/library/native-gradient';

const PetPayForSellersScreen = ({}: Props) => {
  const { t } = useTranslation('screens');
  const route = useRoute();

  return (
    <View>
      <View
        className={withNativeGradientFallback(
          'from-brand-cream to-brand-cream-light items-center bg-gradient-to-b p-6 md:py-12',
        )}
      >
        <Image
          src={catPeace}
          alt={'Cat Doodle'}
          containerClassName={'-m-1.5 size-16 md:size-24'}
          imageClassName={'object-contain'}
        />

        <H1
          title={t('petpay.for_sellers.title')}
          subtitle={t('petpay.for_sellers.description')}
          center={true}
        />

        <View className={'items-center justify-center gap-3 md:flex-row md:gap-1.5'}>
          <PaymentBrands />
          <ReviewsSummaryCard compact={true} />
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-12'}>
        <View className={`mx-auto w-full max-w-screen-xl`}>
          <H2
            title={t('petpay.for_sellers.how_it_works')}
            subtitle={t('petpay.for_sellers.how_it_works_description')}
          />

          <View className={'gap-6 md:flex-row md:gap-12'}>
            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <Reservation />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  1. {t('petpay.for_sellers.reservation')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_sellers.reservation_description')}
                </Text>
              </View>
            </View>

            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <PurchaseAgreement />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  2. {t('petpay.for_sellers.purchase_agreement')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_sellers.purchase_agreement_description')}
                </Text>
              </View>
            </View>

            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <Payment />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  3. {t('petpay.for_sellers.payment')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_sellers.payment_description')}
                </Text>
              </View>
            </View>

            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <Pickup />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  4. {t('petpay.for_sellers.pickup')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_sellers.pickup_description')}
                </Text>
              </View>
            </View>
          </View>

          <View className={'mt-6 md:my-12'} />

          <H2
            id={'vs-other-methods'}
            title={t('petpay.for_sellers.vs_other_methods')}
            subtitle={t('petpay.for_sellers.vs_other_methods_description')}
          />

          <PaymentComparisonTable />

          <View className={'mt-6 md:my-12'} />

          <View
            className={
              'flex-row flex-wrap items-center justify-center gap-3 md:flex-nowrap md:gap-3 lg:gap-6'
            }
          >
            <Image
              src={bankidSe}
              alt={'BankID SE Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
            <Image
              src={mitid}
              alt={'MitID Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain p-1 md:p-2'}
            />
            <Image
              src={ftn}
              alt={'FTN Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain p-2.5 md:p-5'}
            />
            <Image
              src={bankidNo}
              alt={'BankID NO Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
            <Image
              src={stripe}
              alt={'Stripe Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
            <Image
              src={handelsbanken}
              alt={'Handelsbanken Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
          </View>

          <Text className={'text-brand-brown-dark mt-6 text-center'}>
            {t('petpay.for_sellers.cta_text')}
          </Text>

          <View className={'mt-6 flex-row justify-center'}>
            <Button
              text={t('petpay.for_sellers.new_listing')}
              icon={falAngleRight}
              color={'brown'}
              href={route('me.listings.index')}
            />
          </View>

          <View className={'mt-6 md:my-12'} />

          <ReviewsSegment role={'seller'} />
        </View>
      </View>
    </View>
  );
};

const PaymentComparisonTable = () => {
  const { t } = useTranslation('screens');

  const tableRows: PaymentComparisonRow[] = [
    {
      title: t('petpay.for_sellers.guaranteed_payments'),
      description: t('petpay.for_sellers.guaranteed_payments_description'),
    },
    {
      title: t('petpay.for_sellers.verified_users'),
      description: t('petpay.for_sellers.verified_users_description'),
    },
    {
      title: t('petpay.for_sellers.deposit_without_hassle'),
      description: t('petpay.for_sellers.deposit_without_hassle_description'),
    },
    {
      title: t('petpay.for_sellers.cancel_commitment'),
      description: t('petpay.for_sellers.cancel_commitment_description'),
    },
    {
      title: t('petpay.for_sellers.proof_of_funds'),
      description: t('petpay.for_sellers.proof_of_funds_description'),
    },
    {
      title: t('petpay.for_sellers.flexible_payment_options'),
      description: t('petpay.for_sellers.flexible_payment_options_description'),
    },
    {
      title: t('petpay.for_sellers.dispute_support'),
      description: t('petpay.for_sellers.dispute_support_description'),
    },
    {
      title: t('petpay.for_sellers.everything_in_one_place'),
      description: t('petpay.for_sellers.everything_in_one_place_description'),
    },
  ];

  return (
    <View
      className={
        'border-brand-brown-dark/15 -mx-3 md:mx-0 md:overflow-hidden md:rounded-lg md:border'
      }
    >
      <View className={'flex-row'}>
        <View className={'w-3/4 md:w-3/5'} />
        <View className={'flex-1 items-center justify-center p-2 md:p-3'}>
          <SvgImage
            src={logo}
            alt={'Get a Pet Logo'}
            containerClassName={'aspect-logo h-4 md:h-8'}
          />
        </View>
        <View className={'flex-1 items-center justify-center p-2 md:p-3'}>
          <Text className={'text-brand-brown-dark text-center text-xs font-semibold md:text-base'}>
            {t('petpay.for_sellers.other_methods')}
          </Text>
        </View>
      </View>

      {tableRows.map((row, index) => (
        <TableRow key={index} row={row} index={index} />
      ))}
    </View>
  );
};

interface PaymentComparisonRow {
  title: string;
  description: string;
}

const TableRow = ({ row, index }: { row: PaymentComparisonRow; index: number }) => {
  return (
    <View
      className={clsx('flex-row rounded', {
        'bg-brand-cream/70': index % 2 === 0,
      })}
    >
      <TableCell row={row} type={'description'} />
      <TableCell row={row} type={'petpay'} />
      <TableCell row={row} type={'other'} />
    </View>
  );
};

const TableCell = ({
  row,
  type,
}: {
  row: PaymentComparisonRow;
  type: 'description' | 'petpay' | 'other';
}) => {
  return (
    <View
      className={clsx('items-center justify-center', {
        'w-3/4 md:w-3/5': type === 'description',
        'flex-1': type !== 'description',
      })}
    >
      {type === 'description' ? (
        <View className={'w-full p-3'}>
          <Text className={'text-brand-brown-dark w-full font-bold md:text-lg'}>{row.title}</Text>
          <Text className={'text-brand-brown-dark/70 mt-0.5 w-full text-sm md:mt-1 md:text-base'}>
            {row.description}
          </Text>
        </View>
      ) : (
        <Icon
          icon={type === 'petpay' ? fasCheck : fasTimes}
          className={clsx('size-8 rounded-full p-2 md:size-10 md:p-3', {
            'bg-brand-brown-dark/10 text-brand-brown-dark': type === 'other',
            'bg-brand-green text-brand-green-light': type === 'petpay',
          })}
        />
      )}
    </View>
  );
};

export { PetPayForSellersScreen };
